import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Card,
  Stack,
  Button,
  CircularProgress,
  IconButton,
  TextField,
  InputAdornment,
  Divider,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SportsBasketballRoundedIcon from "@mui/icons-material/SportsBasketballRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { useNavigate } from "react-router-dom";
import { useApp } from "../../context/AppContext";

const statusOrder = {
  live: 0,
  scheduled: 1,
  finished: 2,
  cancelled: 3,
};

const statusMap = {
  live: {
    label: "EN VIVO",
    bg: "rgba(211,47,47,0.10)",
    color: "error.main",
  },
  scheduled: {
    label: "PENDIENTE",
    bg: "rgba(140,15,44,0.08)",
    color: "primary.main",
  },
  finished: {
    label: "FINALIZADO",
    bg: "rgba(25,118,210,0.08)",
    color: "info.main",
  },
  cancelled: {
    label: "CANCELADO",
    bg: "rgba(100,100,100,0.10)",
    color: "text.secondary",
  },
};

function formatDateHeader(date) {
  return new Date(`${date}T12:00:00`).toLocaleDateString("es-EC", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function formatDateButton(date) {
  const d = new Date(`${date}T12:00:00`);
  const weekday = d.toLocaleDateString("es-EC", {
    weekday: "short",
  });
  const day = d.toLocaleDateString("es-EC", {
    day: "2-digit",
  });

  return `${weekday.replace(".", "").toUpperCase()} ${day}`;
}

function formatDateLabel(date, time) {
  const today = new Date();
  const matchDate = new Date(`${date}T12:00:00`);

  const isToday =
    today.getFullYear() === matchDate.getFullYear() &&
    today.getMonth() === matchDate.getMonth() &&
    today.getDate() === matchDate.getDate();

  if (isToday) {
    return `Hoy · ${time}`;
  }

  return `${matchDate.toLocaleDateString("es-EC", {
    day: "numeric",
    month: "short",
  })} · ${time}`;
}

function getScoreLabel(match) {
  if (match.status === "scheduled") {
    return "- -";
  }

  return `${match.score_local ?? 0} - ${match.score_visitor ?? 0}`;
}

export default function AdminHome() {
  const navigate = useNavigate();
  const { clearSession } = useApp();

  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [selectedDate, setSelectedDate] = useState("");

  const fetchMatches = async (reload = false) => {
    try {
      setError("");
      if (reload) {
        setReloading(true);
      } else {
        setLoading(true);
      }

      const access = localStorage.getItem("access");

      const res = await fetch("/matches/list/", {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(access ? { Authorization: `Bearer ${access}` } : {}),
        },
      });

      if (res.status === 401) {
        clearSession();
        navigate("/admin-login", { replace: true });
        return;
      }

      if (!res.ok) {
        throw new Error("No se pudo cargar la lista de partidos");
      }

      const data = await res.json();
      const safeData = Array.isArray(data) ? data : [];
      setMatches(safeData);

      const available = [...new Set(safeData.map((match) => match.date))].sort();
      const today = new Date().toISOString().slice(0, 10);

      if (available.length) {
        if (available.includes(selectedDate)) {
          return;
        }

        if (available.includes(today)) {
          setSelectedDate(today);
        } else {
          setSelectedDate(available[0]);
        }
      } else {
        setSelectedDate("");
      }
    } catch (err) {
      setError(err.message || "No se pudo cargar la lista de partidos");
    } finally {
      setLoading(false);
      setReloading(false);
    }
  };

  useEffect(() => {
    fetchMatches();
  }, []);

  const availableDates = useMemo(() => {
    return [...new Set(matches.map((match) => match.date))].sort();
  }, [matches]);

  const filteredMatches = useMemo(() => {
    const q = search.trim().toLowerCase();

    return [...matches]
      .filter((match) => {
        if (!selectedDate) return false;
        if (match.date !== selectedDate) return false;

        if (!q) return true;

        return [
          match.team_local,
          match.team_visitor,
          match.category,
          match.venue,
          match.status,
          match.date,
          match.time,
        ]
          .join(" ")
          .toLowerCase()
          .includes(q);
      })
      .sort((a, b) => {
        const statusCompare = (statusOrder[a.status] ?? 99) - (statusOrder[b.status] ?? 99);
        if (statusCompare !== 0) return statusCompare;

        const aDate = new Date(`${a.date}T${a.time || "00:00"}`);
        const bDate = new Date(`${b.date}T${b.time || "00:00"}`);
        return aDate - bDate;
      });
  }, [matches, search, selectedDate]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", maxWidth: 1080, mx: "auto" }}>
      <Stack
        direction={{ xs: "column", lg: "row" }}
        alignItems={{ xs: "stretch", lg: "center" }}
        justifyContent="space-between"
        spacing={1.8}
        sx={{ mb: 2.4 }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: { xs: "1.6rem", md: "2rem" },
              fontWeight: 800,
              color: "text.primary",
              lineHeight: 1.1,
            }}
          >
            Gestión de marcadores
          </Typography>
        </Box>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar equipo, sede o categoría"
            size="small"
            sx={{
              minWidth: { xs: "100%", sm: 300 },
              backgroundColor: "#fff",
              borderRadius: "12px",
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
          />

          <IconButton
            onClick={() => fetchMatches(true)}
            disabled={reloading}
            sx={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              backgroundColor: "#fff",
              border: "1px solid rgba(140,15,44,0.12)",
              "&:hover": {
                backgroundColor: "rgba(140,15,44,0.04)",
              },
            }}
          >
            {reloading ? (
              <CircularProgress size={18} />
            ) : (
              <RefreshRoundedIcon sx={{ color: "primary.main" }} />
            )}
          </IconButton>
        </Stack>
      </Stack>

      {availableDates.length ? (
        <Card
          elevation={0}
          sx={{
            borderRadius: "24px",
            border: "1px solid rgba(140,15,44,0.12)",
            p: { xs: 1.2, sm: 1.5 },
            mb: 2.2,
            backgroundColor: "#fff",
          }}
        >
          <Stack spacing={1.2}>
            <Stack direction="row" alignItems="center" spacing={0.8}>
              <EventRoundedIcon sx={{ color: "primary.main", fontSize: 20 }} />
              <Typography
                sx={{
                  fontWeight: 800,
                  color: "text.primary",
                  fontSize: ".95rem",
                }}
              >
                Selecciona el día del campeonato
              </Typography>
            </Stack>

            <Box
              sx={{
                display: "flex",
                gap: 1,
                overflowX: "auto",
                pb: 0.2,
                "&::-webkit-scrollbar": {
                  height: 6,
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(140,15,44,0.18)",
                  borderRadius: "999px",
                },
              }}
            >
              {availableDates.map((date) => {
                const active = selectedDate === date;

                return (
                  <Button
                    key={date}
                    onClick={() => setSelectedDate(date)}
                    variant={active ? "contained" : "outlined"}
                    sx={{
                      minWidth: 108,
                      height: 46,
                      borderRadius: "14px",
                      px: 2,
                      flexShrink: 0,
                      fontWeight: 800,
                      borderColor: "rgba(140,15,44,0.18)",
                      color: active ? "#fff" : "primary.main",
                      backgroundColor: active ? "primary.main" : "#fff",
                    }}
                  >
                    {formatDateButton(date)}
                  </Button>
                );
              })}
            </Box>
          </Stack>
        </Card>
      ) : null}

      {error ? (
        <Card
          elevation={0}
          sx={{
            borderRadius: "18px",
            border: "1px solid rgba(211,47,47,0.16)",
            p: 2.5,
            mb: 2,
          }}
        >
          <Typography sx={{ color: "error.main", fontWeight: 700 }}>{error}</Typography>
        </Card>
      ) : null}

      {!availableDates.length ? (
        <Card
          elevation={0}
          sx={{
            borderRadius: "24px",
            border: "1px solid rgba(140,15,44,0.12)",
            p: { xs: 2.6, sm: 3.2 },
            textAlign: "center",
            backgroundColor: "#fff",
          }}
        >
          <Typography sx={{ fontWeight: 800, fontSize: "1.08rem" }}>
            No hay partidos disponibles
          </Typography>
          <Typography sx={{ mt: 0.8, color: "text.secondary" }}>
            Cuando registres partidos en el calendario aparecerán aquí
          </Typography>
        </Card>
      ) : !filteredMatches.length ? (
        <Card
          elevation={0}
          sx={{
            borderRadius: "24px",
            border: "1px solid rgba(140,15,44,0.12)",
            p: { xs: 2.6, sm: 3.2 },
            textAlign: "center",
            backgroundColor: "#fff",
          }}
        >
          <Typography sx={{ fontWeight: 800, fontSize: "1.08rem" }}>
            No hay partidos para este filtro
          </Typography>
          <Typography sx={{ mt: 0.8, color: "text.secondary" }}>
            Cambia el día o ajusta la búsqueda para encontrar otro partido
          </Typography>
        </Card>
      ) : (
        <Stack spacing={2}>
          <Card
            elevation={0}
            sx={{
              borderRadius: "24px",
              border: "1px solid rgba(140,15,44,0.12)",
              p: { xs: 1.6, sm: 2 },
              backgroundColor: "#fff",
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              spacing={0.8}
            >
              <Typography
                sx={{
                  fontWeight: 900,
                  fontSize: { xs: "1rem", sm: "1.12rem" },
                  color: "primary.main",
                  textTransform: "capitalize",
                }}
              >
                {formatDateHeader(selectedDate)}
              </Typography>

              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: ".92rem",
                  color: "text.secondary",
                }}
              >
                {filteredMatches.length} {filteredMatches.length === 1 ? "partido" : "partidos"}
              </Typography>
            </Stack>
          </Card>

          <Stack spacing={1.5}>
            {filteredMatches.map((match) => {
              const statusStyle = statusMap[match.status] || statusMap.scheduled;

              return (
                <Card
                  key={match.id}
                  elevation={0}
                  sx={{
                    borderRadius: "24px",
                    border: "1px solid rgba(140,15,44,0.12)",
                    p: { xs: 1.5, sm: 2 },
                    backgroundColor: "#fff",
                    transition: "transform .18s ease, box-shadow .18s ease, border-color .18s ease",
                    "&:hover": {
                      transform: "translateY(-1px)",
                      boxShadow: "0 10px 24px rgba(0,0,0,0.05)",
                      borderColor: "rgba(140,15,44,0.22)",
                    },
                  }}
                >
                  <Stack spacing={1.4}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                      <Box
                        sx={{
                          px: 1.2,
                          py: 0.55,
                          borderRadius: "999px",
                          backgroundColor: statusStyle.bg,
                          color: statusStyle.color,
                          fontWeight: 800,
                          fontSize: ".78rem",
                          lineHeight: 1,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {statusStyle.label}
                      </Box>

                      <Typography
                        sx={{
                          color: "text.secondary",
                          fontSize: ".82rem",
                          fontWeight: 700,
                          textAlign: "right",
                        }}
                      >
                        {formatDateLabel(match.date, match.time)}
                      </Typography>
                    </Stack>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr auto 1fr" },
                        alignItems: "center",
                        gap: { xs: 1.2, sm: 1.6 },
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent={{ xs: "center", sm: "flex-start" }}
                        sx={{ minWidth: 0 }}
                      >
                        <Box
                          component="img"
                          src={match.team_local_logo || ""}
                          alt={match.team_local}
                          sx={{
                            width: 44,
                            height: 44,
                            objectFit: "contain",
                            flexShrink: 0,
                            display: match.team_local_logo ? "block" : "none",
                          }}
                        />
                        <Typography
                          sx={{
                            fontWeight: 800,
                            fontSize: { xs: ".98rem", sm: "1rem" },
                            textAlign: { xs: "center", sm: "left" },
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {match.team_local}
                        </Typography>
                      </Stack>

                      <Box
                        sx={{
                          minWidth: { xs: "100%", sm: 176 },
                          px: 1.6,
                          py: 1.15,
                          borderRadius: "18px",
                          backgroundColor: "rgba(140,15,44,0.05)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 1,
                        }}
                      >
                        <SportsBasketballRoundedIcon
                          sx={{
                            color: match.status === "live" ? "error.main" : "primary.main",
                            fontSize: 20,
                          }}
                        />
                        <Typography
                          sx={{
                            fontWeight: 900,
                            fontSize: { xs: "1.2rem", sm: "1.3rem" },
                            color: "primary.main",
                            letterSpacing: ".04em",
                          }}
                        >
                          {getScoreLabel(match)}
                        </Typography>
                      </Box>

                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent={{ xs: "center", sm: "flex-end" }}
                        sx={{ minWidth: 0 }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 800,
                            fontSize: { xs: ".98rem", sm: "1rem" },
                            textAlign: { xs: "center", sm: "right" },
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {match.team_visitor}
                        </Typography>
                        <Box
                          component="img"
                          src={match.team_visitor_logo || ""}
                          alt={match.team_visitor}
                          sx={{
                            width: 44,
                            height: 44,
                            objectFit: "contain",
                            flexShrink: 0,
                            display: match.team_visitor_logo ? "block" : "none",
                          }}
                        />
                      </Stack>
                    </Box>

                    <Divider />

                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={{ xs: 0.9, sm: 1.2 }}
                      justifyContent="space-between"
                      alignItems={{ xs: "stretch", sm: "center" }}
                    >
                      <Stack direction="row" spacing={1.2} flexWrap="wrap" useFlexGap>
                        <Stack direction="row" spacing={0.6} alignItems="center">
                          <PlaceRoundedIcon sx={{ fontSize: 18, color: "primary.main" }} />
                          <Typography sx={{ fontSize: ".9rem", color: "text.secondary", fontWeight: 700 }}>
                            {match.venue}
                          </Typography>
                        </Stack>

                        <Stack direction="row" spacing={0.6} alignItems="center">
                          <AccessTimeRoundedIcon sx={{ fontSize: 18, color: "primary.main" }} />
                          <Typography sx={{ fontSize: ".9rem", color: "text.secondary", fontWeight: 700 }}>
                            {match.category}
                          </Typography>
                        </Stack>
                      </Stack>

                      <Button
                        onClick={() => navigate(`/admin/matches/live/${match.id}`)}
                        variant="contained"
                        endIcon={<ArrowForwardRoundedIcon />}
                        sx={{
                          height: 44,
                          minWidth: { xs: "100%", sm: 198 },
                          borderRadius: "12px",
                          fontWeight: 800,
                        }}
                      >
                        Abrir marcador
                      </Button>
                    </Stack>
                  </Stack>
                </Card>
              );
            })}
          </Stack>
        </Stack>
      )}
    </Box>
  );
}